export type AcquisitionChannel =
  | "AI Assistant"
  | "Organic Search"
  | "Paid Search"
  | "Organic Social"
  | "Referral"
  | "Direct"
  | "Other";

export interface AcquisitionContext {
  landingPage: string;
  acquisitionChannel: AcquisitionChannel;
  acquisitionSource: string;
  acquisitionMedium: string;
  acquisitionCampaign: string;
  referrerHost: string;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    __BELLA_ROSE_HEALTH_AD_LANDING__?: boolean;
    __BELLA_ROSE_ANALYTICS_ALLOWED__?: boolean;
  }
}

const STORAGE_KEY = "bella-rose-first-touch-v1";
const SESSION_EVENT_KEY = "bella-rose-acquisition-session-reported-v1";
const reportedLeadTokens = new Set<string>();
let lastTrackedPath = typeof window === "undefined" ? "" : `${window.location.pathname}${window.location.search}`;

const AI_HOSTS = [
  "chatgpt.com",
  "chat.openai.com",
  "perplexity.ai",
  "gemini.google.com",
  "copilot.microsoft.com",
  "claude.ai",
  "poe.com",
  "you.com",
  "meta.ai",
  "phind.com",
];

const SEARCH_HOSTS = [
  "google.",
  "bing.com",
  "search.yahoo.",
  "duckduckgo.com",
  "search.brave.com",
  "yandex.",
  "baidu.com",
  "ecosia.org",
];

const SOCIAL_HOSTS = [
  "instagram.com",
  "facebook.com",
  "tiktok.com",
  "snapchat.com",
  "linkedin.com",
  "x.com",
  "twitter.com",
  "youtube.com",
];

function clean(value: string | null | undefined, maxLength: number) {
  return (value || "").trim().slice(0, maxLength);
}

function hostnameFromReferrer(referrer: string) {
  if (!referrer) return "";
  try {
    return new URL(referrer).hostname.toLowerCase().replace(/^www\./, "").slice(0, 255);
  } catch {
    return "";
  }
}

function matchesHost(hostname: string, candidates: string[]) {
  return candidates.some(candidate => candidate.endsWith(".")
    ? hostname.includes(candidate)
    : hostname === candidate || hostname.endsWith(`.${candidate}`));
}

function isAiSource(value: string) {
  const normalized = value.toLowerCase();
  return ["chatgpt", "openai", "perplexity", "gemini", "copilot", "claude", "poe", "you.com", "meta ai", "phind"]
    .some(source => normalized.includes(source));
}

export function classifyAcquisition(input: {
  referrerHost: string;
  utmSource: string;
  utmMedium: string;
  hasGoogleClickId: boolean;
}): AcquisitionChannel {
  const source = input.utmSource.toLowerCase();
  const medium = input.utmMedium.toLowerCase();

  if (isAiSource(source) || matchesHost(input.referrerHost, AI_HOSTS)) return "AI Assistant";
  if (input.hasGoogleClickId || ["cpc", "ppc", "paid", "paid_search", "paidsearch"].includes(medium)) return "Paid Search";
  if (["organic", "seo"].includes(medium) || matchesHost(input.referrerHost, SEARCH_HOSTS)) return "Organic Search";
  if (["social", "organic_social"].includes(medium) || matchesHost(input.referrerHost, SOCIAL_HOSTS)) return "Organic Social";
  if (source || medium || input.referrerHost) return "Referral";
  return "Direct";
}

function captureCurrentAcquisition(): AcquisitionContext {
  const params = new URLSearchParams(window.location.search);
  const referrerHost = hostnameFromReferrer(document.referrer);
  const utmSource = clean(params.get("utm_source"), 120);
  const utmMedium = clean(params.get("utm_medium"), 120);

  return {
    landingPage: clean(window.location.pathname, 500) || "/",
    acquisitionChannel: classifyAcquisition({
      referrerHost,
      utmSource,
      utmMedium,
      hasGoogleClickId: params.has("gclid") || params.has("gbraid") || params.has("wbraid"),
    }),
    acquisitionSource: utmSource || referrerHost || "direct",
    acquisitionMedium: utmMedium || (referrerHost ? "referral" : "none"),
    acquisitionCampaign: clean(params.get("utm_campaign"), 255),
    referrerHost,
  };
}

export function getFirstTouchAcquisition(): AcquisitionContext {
  if (typeof window === "undefined") {
    return {
      landingPage: "/",
      acquisitionChannel: "Other",
      acquisitionSource: "unknown",
      acquisitionMedium: "unknown",
      acquisitionCampaign: "",
      referrerHost: "",
    };
  }

  const saved = window.sessionStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      return JSON.parse(saved) as AcquisitionContext;
    } catch {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  const captured = captureCurrentAcquisition();
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  return captured;
}

export function initializeAcquisitionTracking() {
  if (typeof window === "undefined") return undefined;
  const acquisition = getFirstTouchAcquisition();

  if (!window.sessionStorage.getItem(SESSION_EVENT_KEY)) {
    window.gtag?.("event", "acquisition_session", {
      acquisition_channel: acquisition.acquisitionChannel,
      acquisition_source: acquisition.acquisitionSource,
      acquisition_medium: acquisition.acquisitionMedium,
      acquisition_campaign: acquisition.acquisitionCampaign || undefined,
      landing_page: acquisition.landingPage,
      referrer_host: acquisition.referrerHost || undefined,
    });
    window.sessionStorage.setItem(SESSION_EVENT_KEY, "1");
  }

  return acquisition;
}

export function trackSpaPageView(path: string) {
  if (typeof window === "undefined" || !path || path === lastTrackedPath) return false;
  if (window.__BELLA_ROSE_HEALTH_AD_LANDING__) return false;
  lastTrackedPath = path;
  window.gtag?.("event", "page_view", {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  });
  return true;
}

export function trackPersistedLead(input: {
  token: string;
  service: string;
  location: string;
  method: "website_form" | "chat";
}) {
  if (typeof window === "undefined" || !input.token || reportedLeadTokens.has(input.token)) return false;
  if (window.__BELLA_ROSE_HEALTH_AD_LANDING__ && !window.__BELLA_ROSE_ANALYTICS_ALLOWED__) return false;
  reportedLeadTokens.add(input.token);
  const acquisition = getFirstTouchAcquisition();
  window.gtag?.("event", "generate_lead", {
    lead_type: input.method,
    service: clean(input.service, 120),
    form_location: clean(input.location, 120),
    acquisition_channel: acquisition.acquisitionChannel,
    acquisition_source: acquisition.acquisitionSource,
    acquisition_medium: acquisition.acquisitionMedium,
    acquisition_campaign: acquisition.acquisitionCampaign || undefined,
    landing_page: acquisition.landingPage,
    referrer_host: acquisition.referrerHost || undefined,
  });
  return true;
}

export function resetAcquisitionTrackingForTests() {
  reportedLeadTokens.clear();
  lastTrackedPath = "";
}
